// EarnPointsPage.jsx
// Earn Points — independent from PointAdjustmentsPage.
//
// ── Two entry flows ───────────────────────────────────────────────────────
//
// FLOW 1 — User enters Currency amount, tabs away:
//   1. Call convertCurrencyToPoints(currencyValue) → raw API result = A
//   2. Unity Points field  = (A / 100) * 3   ← displayed + sent to unity adjustment
//   3. Tier Points         = round(|A / 100|), sign preserved  ← displayed + sent to tier adjustment
//
// FLOW 2 — User enters Unity Points (A) directly, tabs away:
//   1. Compute intermediate = (A / 3) * 100
//   2. Call convertPointsToCurrency(intermediate) → fill Currency field from result
//   3. Unity Points sent to unity adjustment = A  (the entered value)
//   4. Tier Points = round(|A / 3|), sign preserved               ← displayed + sent to tier adjustment
//
// Tier adjustment is only posted when "Update Tier Credits" = Yes.

import React, { useState, useEffect, useRef } from 'react';
import '../../styles/operaCloud.css';
import './EarnPointsPage.css';
import {
  postUnityPointsAdjustment,
  postTierPointsAdjustment,
  getLocation,
  getCurrencies,
  convertCurrencyToPoints,
  convertPointsToCurrency,
} from '../point-adjustments/operaExpClient';
import useAuthSafe from '../../auth/useAuthSafe';
import { useAuthorization } from '../../auth/useAuthorization';
import UnauthorizedPage from '../../components/UnauthorizedPage';

export default function EarnPointsPage({ isModal = false, onClose = null }) {
  const auth = useAuthSafe();
  const { canAccessEarnPoints, isAuthLoading } = useAuthorization();
  const givenName  = auth.user?.profile?.given_name  ?? '';
  const familyName = auth.user?.profile?.family_name ?? '';
  const adjustmentUser = givenName && familyName ? `${givenName}.${familyName}` : undefined;

  // ─── State ───────────────────────────────────────────────────────────────
  const [pmsHotelId,             setPmsHotelId]             = useState('');
  const [propertyCode,           setPropertyCode]           = useState('');
  const [propertyName,           setPropertyName]           = useState('');
  const [nameLoading,            setNameLoading]            = useState(false);
  const [nameError,              setNameError]              = useState(null);
  const [isValidProperty,        setIsValidProperty]        = useState(false);
  const [memberId,               setMemberId]               = useState('');
  const [currencyCode,           setCurrencyCode]           = useState('USD');
  const [propertyCurrency,       setPropertyCurrency]       = useState('USD');
  const [currencyValue,          setCurrencyValue]          = useState('');
  const [exchangeDate,           setExchangeDate]           = useState(new Date().toISOString().split('T')[0]);
  const [reason,                 setReason]                 = useState('');
  const [updateTierCredits,      setUpdateTierCredits]      = useState('No');

  // unityPoints — either (A/100)*3 from currency flow OR entered directly by user
  // tierPoints  — either A/100 from currency flow OR A/3 from points flow
  // Both are strings so they can be bound to inputs
  const [unityPoints,            setUnityPoints]            = useState('');
  const [tierPoints,             setTierPoints]             = useState('');

  const [converting,             setConverting]             = useState(false);
  const [conversionError,        setConversionError]        = useState('');
  const [loading,                setLoading]                = useState(false);
  const [successResponse,        setSuccessResponse]        = useState(null);
  const [errorMessage,           setErrorMessage]           = useState(null);
  const [showToast,              setShowToast]              = useState(false);
  const [toastData,              setToastData]              = useState(null);
  const [currencies,             setCurrencies]             = useState([]);
  const [currenciesLoading,      setCurrenciesLoading]      = useState(false);

  const dateInputRef = useRef(null);
  // Track which field the user manually typed in.
  // Auto-filled values (set programmatically) must NOT re-trigger the other flow on blur.
  const currencyManualEdit = useRef(false);
  const unityManualEdit    = useRef(false);

  // Form is valid when both fields are populated + other required fields
  const isFormValid =
    memberId &&
    currencyValue &&
    propertyCode &&
    reason &&
    unityPoints !== '';

  // ─── FLOW 1: Currency → Points (on blur of currency field) ───────────────
  // Calls convertCurrencyToPoints(currencyValue) → A
  //   Unity Points = (A / 100) * 3
  //   Tier Points  = A / 100
  const handleCurrencyValueBlur = async () => {
    // Only run if the user actually typed in this field (not auto-filled by Flow 2)
    if (!currencyManualEdit.current) return;
    currencyManualEdit.current = false;

    setConversionError('');
    setUnityPoints('');
    setTierPoints('');

    if (!currencyValue || converting || !propertyCode) return;

    setConverting(true);
    try {
      const numericCurrency = Number(currencyValue);
      const absCurrency = Math.abs(numericCurrency);
      const sign = numericCurrency < 0 ? -1 : 1;

      const result = await convertCurrencyToPoints({
        memberId,
        currencyValue: absCurrency,
        currencyCode,
        propertyCode,
        exchangeDate, // UI-selected date
      });

      if (result?.convertedValue !== undefined) {
        const A = sign * result.convertedValue; // raw API result
        // Unity Points = (A / 100) * 3 — auto-filled, NOT a manual edit.
        // Decimals are kept as-is: they are valid when posting the adjustment and are
        // rounded only when passed to the points-to-currency endpoint.
        setUnityPoints(((A / 100) * 3).toString());
        unityManualEdit.current = false;
        // Tier Points = round(|A / 100|) — always a whole number, sign preserved
        const rawTier1 = A / 100;
        const roundedTier1 = rawTier1 >= 0
          ? Math.round(rawTier1)
          : -Math.round(Math.abs(rawTier1));
        setTierPoints(roundedTier1.toString());
      }
    } catch (err) {
      console.error('Currency to points conversion error');
      const detail = err?.response?.detail || '';
      setConversionError(
        detail
          ? `Error converting currency to points — ${detail}`
          : 'Error converting currency to points'
      );
      setCurrencyValue('');
    } finally {
      setConverting(false);
    }
  };

  // ─── FLOW 2: Points → Currency (on blur of unity points field) ───────────
  // User enters Unity Points = A directly.
  //   Intermediate for API call = (A / 3) * 100
  //   Currency field filled from convertPointsToCurrency(intermediate)
  //   Unity Points sent to unity adjustment = A  (entered value, unchanged)
  //   Tier Points = A / 3
  const handleUnityPointsBlur = async () => {
    // Only run if the user actually typed in this field (not auto-filled by Flow 1)
    if (!unityManualEdit.current) return;
    unityManualEdit.current = false;

    setConversionError('');
    setTierPoints('');
    setCurrencyValue('');

    if (!unityPoints || converting || !propertyCode) return;

    if (!Number.isInteger(Number(unityPoints))) {
      setConversionError('Points must be a whole number. Decimal values are not allowed.');
      setTierPoints('');
      setCurrencyValue('');
      return;
    }

    setConverting(true);
    try {
      const A = Number(unityPoints);
      const absA = Math.abs(A);
      const sign = A < 0 ? -1 : 1;

      // Intermediate = (A / 3) * 100  — round up to nearest integer before calling
      // the endpoint because /pointsToCurrency does not accept decimal values
      const intermediate = Math.ceil((absA / 3) * 100);

      const result = await convertPointsToCurrency({
        memberId,
        points: intermediate,
        currencyCode,
        propertyCode,
        exchangeDate, // UI-selected date
      });

      if (result?.convertedValue !== undefined) {
        // Fill the currency field with the converted value — auto-filled, NOT a manual edit
        setCurrencyValue((sign * result.convertedValue).toString());
        currencyManualEdit.current = false;
      }

      // Tier Points = round(|A / 3|) — always a whole number, sign preserved
      const rawTier2 = A / 3;
      const roundedTier2 = rawTier2 >= 0
        ? Math.round(rawTier2)
        : -Math.round(Math.abs(rawTier2));
      setTierPoints(roundedTier2.toString());
    } catch (err) {
      console.error('Points to currency conversion error');
      const detail = err?.response?.detail || '';
      setConversionError(
        detail
          ? `Error converting points to currency — ${detail}`
          : 'Error converting points to currency'
      );
      setUnityPoints('');
    } finally {
      setConverting(false);
    }
  };

  // ─── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    setLoading(true);
    setSuccessResponse(null);
    setErrorMessage(null);

    try {
      // Determine the unity points to send.
      // USD property: unityPoints is posted as-is (decimals are allowed for posting).
      // Non-USD property (whatever currency is selected on the form):
      //   unity -> raw points -> property currency -> USD raw points -> unity
      // Going through the property currency means a third currency (e.g. INR on an EUR
      // property) is first expressed in the property currency, and only then re-valued
      // as USD. The conversion endpoints work on the raw points scale (A), while the
      // unity field is (A / 100) * 3, so the scale is mapped back and forth.
      // All extra conversions use the exchange date selected on the form.
      let unityPointsToSend = Number(unityPoints);
      if (propertyCurrency && propertyCurrency !== 'USD') {
        const numericUnityPoints = Number(unityPoints);
        const sign = numericUnityPoints < 0 ? -1 : 1;

        // Back to the raw scale (inverse of the (A / 100) * 3 mapping). The points-to-currency
        // endpoint rejects decimals, so round up here (same as the blur flow). toFixed guards
        // against floating-point noise such as 8814.000000000001 becoming 8815.
        const rawPoints = Math.ceil(Number(((Math.abs(numericUnityPoints) / 3) * 100).toFixed(6)));

        const pointsToPropertyCurrency = await convertPointsToCurrency({
          memberId,
          points: rawPoints,
          currencyCode: propertyCurrency,
          propertyCode,
          exchangeDate,
        });

        if (pointsToPropertyCurrency?.convertedValue === undefined) {
          throw new Error('Currency conversion failed: could not convert points to property currency. Please try again.');
        }

        const propertyCurrencyToUsd = await convertCurrencyToPoints({
          memberId,
          currencyValue: pointsToPropertyCurrency.convertedValue,
          currencyCode: 'USD',
          propertyCode,
          exchangeDate,
        });

        if (propertyCurrencyToUsd?.convertedValue === undefined) {
          throw new Error('Currency conversion failed: could not convert property currency to USD points. Please try again.');
        }

        // Same mapping as the USD flow: unity = (A / 100) * 3
        unityPointsToSend = sign * (propertyCurrencyToUsd.convertedValue / 100) * 3;
      }

      // Post unity adjustment — tier points remain unchanged.
      const unityResp = await postUnityPointsAdjustment({
        memberId,
        points: unityPointsToSend,
        propertyCode,
        exchangeDate: new Date().toISOString(), // always current date & time at the moment of submission
        reason,
        adjustmentUser
      });

      let tierResp = null;
      if (updateTierCredits === 'Yes') {
        try {
          // tierPoints is already rounded (round with sign) when stored — re-apply
          // Math.round here as a safety guard in case state is ever set from another path.
          const rawTierPost = Number(tierPoints);
          const safeTierPoints = rawTierPost >= 0
            ? Math.round(rawTierPost)
            : -Math.round(Math.abs(rawTierPost));
          tierResp = await postTierPointsAdjustment({
            memberId,
            points: safeTierPoints,   // round(A/100) or round(A/3), sign preserved
            propertyCode,
            adjustmentDateTime: new Date().toISOString(), // always current date & time at the moment of submission
            reason,
            adjustmentUser
          });
        } catch (tierErr) {
          console.warn('Tier adjustment failed');
        }
      }

      setSuccessResponse({ unity: unityResp, tier: tierResp });

      try {
        const unityBal = (unityResp && (unityResp.updatedBalance ?? unityResp.balance ?? unityResp.updatedBalanceAmount))?.toString();
        const tierBal  = (tierResp  && (tierResp.updatedBalance  ?? tierResp.balance  ?? tierResp.updatedAmount))?.toString();
        setToastData({ unity: unityBal || null, tier: tierBal || null });
        setShowToast(true);
      } catch (_) { /* ignore toast errors */ }

      // Reset editable fields
      setCurrencyValue('');
      setUnityPoints('');
      setTierPoints('');
      setExchangeDate(new Date().toISOString().split('T')[0]);
      setReason('');
      setUpdateTierCredits('No');
    } catch (err) {
      console.error('Currency Calculator Adjustment Error');

      let msgObj  = null;
      let msgText = 'Failed to process earnings';
      if (err?.response?.error || err?.response?.message) {
        msgObj  = err.response.error || { message: err.response.message };
        msgText = err.response.error?.message || err.response.message || msgText;
      } else if (err?.message) {
        msgObj  = { message: err.message };
        msgText = err.message;
      } else {
        msgObj = { message: msgText };
      }
      setErrorMessage(msgObj);

      try {
        setToastData({ isError: true });
        setShowToast(true);
      } catch (_) { /* ignore */ }
    } finally {
      setLoading(false);
    }
  };

  // ─── Reset ───────────────────────────────────────────────────────────────
  const handleReset = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      setCurrencyCode(propertyCurrency || 'USD');
      setCurrencyValue('');
      currencyManualEdit.current = false;
      setUnityPoints('');
      unityManualEdit.current = false;
      setTierPoints('');
      setExchangeDate(new Date().toISOString().split('T')[0]);
      setReason('');
      setUpdateTierCredits('No');
      setConversionError('');
      setSuccessResponse(null);
      setErrorMessage(null);
    }
  };

  // ─── Date blur: re-run currency → points when exchange date changes ────────
  // Uses e.target.value directly (not state) so we always get the latest date
  // even before React re-renders with the new state.
  // If currencyValue is present: re-convert with new date and refill unity/tier points.
  // If currencyValue is absent:  clear stale points so the form doesn't hold
  //                               values calculated against the old date.
  const handleDateBlur = async (e) => {
    const newDate = e.target.value;
    if (!newDate) return;

    // Always clear computed fields — they were calculated against the old date
    setUnityPoints('');
    setTierPoints('');
    setConversionError('');
    unityManualEdit.current = false;

    // Only re-convert if there is a currency value to work from
    if (!currencyValue || converting || !propertyCode) return;

    setConverting(true);
    try {
      const numericCurrency = Number(currencyValue);
      const absCurrency = Math.abs(numericCurrency);
      const sign = numericCurrency < 0 ? -1 : 1;

      const result = await convertCurrencyToPoints({
        memberId,
        currencyValue: absCurrency,
        currencyCode,
        propertyCode,
        exchangeDate: newDate, // use the new date directly from event
      });

      if (result?.convertedValue !== undefined) {
        const A = sign * result.convertedValue;
        setUnityPoints(((A / 100) * 3).toString());
        unityManualEdit.current = false;
        const rawTier = A / 100;
        const roundedTier = rawTier >= 0
          ? Math.round(rawTier)
          : -Math.round(Math.abs(rawTier));
        setTierPoints(roundedTier.toString());
      }
    } catch (err) {
      console.error('Date change re-conversion error');
      const detail = err?.response?.detail || '';
      setConversionError(
        detail
          ? `Error re-converting after date change — ${detail}`
          : 'Error re-converting after date change'
      );
      // Clear both fields so stale values are not submitted
      setUnityPoints('');
      setTierPoints('');
    } finally {
      setConverting(false);
    }
  };

  // ─── Read URL params ──────────────────────────────────────────────────────
  useEffect(() => {
    const urlParams          = new URLSearchParams(window.location.search);
    const receivedMemberId   = urlParams.get('memberId') || urlParams.get('accountNumber');
    const receivedPmsHotelId = urlParams.get('pmsHotelId');
    if (receivedMemberId)   setMemberId(receivedMemberId);
    if (receivedPmsHotelId) setPmsHotelId(receivedPmsHotelId);
  }, []);

  // ─── Fetch property + currencies ─────────────────────────────────────────
  useEffect(() => {
    if (!canAccessEarnPoints) return;
    let cancelled = false;

    async function fetchPageData(id) {
      if (!id) return;
      setNameLoading(true);
      setCurrenciesLoading(true);
      setNameError(null);
      try {
        const [locationRes, currenciesRes] = await Promise.all([
          getLocation(id),
          getCurrencies()
        ]);
        if (cancelled) return;

        if (locationRes?.locationLegalName && locationRes?.locationCode) {
          setPropertyName(locationRes.locationLegalName);
          setPropertyCode(locationRes.locationCode);
          setPropertyCurrency(locationRes.currency || 'USD');
          setIsValidProperty(true);
        } else {
          setPropertyName('');
          setPropertyCode('');
          setPropertyCurrency('USD');
          setIsValidProperty(false);
        }

        if (Array.isArray(currenciesRes)) {
          const seen = new Set();
          const uniqueCurrencies = currenciesRes
            .filter(c => c.currencyCode != null)
            .sort((a, b) => a.currencyCode.localeCompare(b.currencyCode))
            .filter(c => {
              if (seen.has(c.currencyCode)) return false;
              seen.add(c.currencyCode);
              return true;
            });
          const defaultCurrency = locationRes?.currency || 'USD';
          const currenciesWithDefault = uniqueCurrencies.some(c => c.currencyCode === defaultCurrency)
            ? uniqueCurrencies
            : [{ currencyCode: defaultCurrency }, ...uniqueCurrencies];
          setCurrencies(currenciesWithDefault);
          if (currenciesWithDefault.length > 0) {
            setCurrencyCode(defaultCurrency);
          }
        }
      } catch (err) {
        console.error('Failed to fetch page data for pmsHotelId');
        if (!cancelled) {
          setNameError(err);
          setPropertyName('');
          setPropertyCode('');
          setIsValidProperty(false);
        }
      } finally {
        if (!cancelled) {
          setNameLoading(false);
          setCurrenciesLoading(false);
        }
      }
    }

    if (pmsHotelId) fetchPageData(pmsHotelId);
    return () => { cancelled = true; };
  }, [pmsHotelId, canAccessEarnPoints]);

  // ─── Toast auto-dismiss ───────────────────────────────────────────────────
  useEffect(() => {
    if (!showToast) return;
    const t = setTimeout(() => setShowToast(false), 40000);
    return () => clearTimeout(t);
  }, [showToast]);

  // ─── Access guard ─────────────────────────────────────────────────────────
  if (isAuthLoading) {
    // Auth is still being established (loading, renewing, or re-logging in).
    // Return null — ProtectedApp is showing the appropriate loading/login UI.
    // Never show UnauthorizedPage during a transitional auth state.
    return null;
  }

  if (!canAccessEarnPoints) {
    return <UnauthorizedPage pageName="Earn Points" />;
  }

  // ─── Empty / loading guard ────────────────────────────────────────────────
  const hasMemberAndValidProperty = !!memberId && !!pmsHotelId && isValidProperty;

  if (!hasMemberAndValidProperty) {
    return (
      <div className="ccFullscreen">
        <div className="ccFullscreenBody">
          <div className="ccCard">
            <div className="ccEmptyState">
              {nameLoading ? 'Loading...' : 'No data available'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Form ─────────────────────────────────────────────────────────────────
  const formContent = (
    <div className="ccCard">
      <div className="ccCardBody">
        <form onSubmit={handleSubmit} className="ccForm">

          {/* Property — display only */}
          <div className="ccField">
            <label className="oc-label">Property</label>
            <div className="ccDisplayValue">
              {nameLoading ? 'Loading property...' : (propertyName || propertyCode)}
            </div>
          </div>

          {/* Member ID — display only */}
          <div className="ccField">
            <label className="oc-label">Member ID</label>
            <div className="ccDisplayValue">{memberId}</div>
          </div>

          {/* Unity Points — editable; matches Point Adjustments layout exactly.
               Flow 2: user types here → triggers reverse currency calculation on blur.
               Flow 1: auto-filled after currency conversion. */}
          <div className="ccField">
            <div className="ccFieldLabelRow">
              <label className="oc-label">Calculate Points</label>
              {conversionError && (
                <span className="ccFieldError" role="alert">{conversionError}</span>
              )}
            </div>
            <div className="ccInlineRow">
              {/* Fixed "Points" label cell — identical to Point Adjustments page */}
              <div className="ccDisplayValue">Points</div>
              <input
                type="number"
                className="oc-input"
                value={unityPoints}
                onChange={(e) => {
                  setUnityPoints(e.target.value);
                  unityManualEdit.current = true;
                  // When the user types points manually, clear currency so it can be refilled
                  setCurrencyValue('');
                  currencyManualEdit.current = false;
                  setTierPoints('');
                  setConversionError('');
                }}
                onBlur={handleUnityPointsBlur}
                step="any"
                placeholder="Enter points"
                disabled={loading || converting}
              />
            </div>
            {converting && (
              <div style={{ fontSize: 11, color: 'var(--oc-text-muted)', marginTop: 2 }}>
                Converting…
              </div>
            )}
          </div>

          {/* Currency */}
          <div className="ccField">
            <label className="oc-label">Currency</label>
            <div className="ccInlineRow">
              <select
                className="oc-select"
                value={currencyCode}
                onChange={(e) => {
                  setCurrencyCode(e.target.value);
                  setCurrencyValue('');
                  setUnityPoints('');
                  setTierPoints('');
                  setConversionError('');
                }}
                disabled={loading || currenciesLoading}
              >
                {currenciesLoading ? (
                  <option value="">Loading...</option>
                ) : (
                  currencies.map(c => (
                    <option key={c.currencyCode} value={c.currencyCode}>{c.currencyCode}</option>
                  ))
                )}
              </select>
              <input
                type="number"
                className="oc-input"
                value={currencyValue}
                onChange={(e) => {
                  setCurrencyValue(e.target.value);
                  currencyManualEdit.current = true;
                  // Clear computed values when user types a new amount
                  setUnityPoints('');
                  unityManualEdit.current = false;
                  setTierPoints('');
                  setConversionError('');
                }}
                onBlur={handleCurrencyValueBlur}
                step="0.01"
                placeholder="Enter currency value"
                required
                disabled={loading || converting}
              />
            </div>
          </div>

          {/* Reason for Adjustment */}
          <div className="ccField">
            <label className="oc-label">Reason for Adjustment</label>
            <select
              className="oc-select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              disabled={loading}
            >
              <option value="">Select a reason</option>
              <option value="Missing Earning">Missing Earning</option>
              <option value="Missing Earnings Adjustment (-)">Missing Earnings Adjustment (-)</option>
            </select>
          </div>

          {/* Update Tier Credits — with inline tier points display when Yes */}
          <div className="ccField">
            <label className="oc-label">Update Tier Credits</label>
            <div className="ccTierRow">
              <select
                className="oc-select"
                style={{ width: 110 }}
                value={updateTierCredits}
                onChange={(e) => setUpdateTierCredits(e.target.value)}
                disabled={loading}
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
              {updateTierCredits === 'Yes' && tierPoints !== '' && (
                <span className="ccTierLabel">
                  Tier Credits: {Number(tierPoints).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              )}
            </div>
          </div>

          {/* Currency Exchange Date */}
          <div className="ccField">
            <label className="oc-label">Currency Exchange Date:</label>
            <div className="oc-date-wrap">
              <input
                ref={dateInputRef}
                type="date"
                className="oc-date-input"
                value={exchangeDate}
                onChange={(e) => {
                  setExchangeDate(e.target.value);
                  // Trigger re-conversion on every complete date change (covers both
                  // keyboard entry and native calendar picker selection).
                  // handleDateBlur uses e.target.value directly so we pass a synthetic
                  // event-like object with the new value.
                  if (e.target.value && e.target.value.length === 10) {
                    handleDateBlur(e);
                  }
                }}
                onBlur={handleDateBlur}
                required
                disabled={loading}
              />
              <button
                type="button"
                className="oc-cal-btn"
                disabled={loading}
                aria-label="Open calendar"
                onClick={() => {
                  if (!dateInputRef.current) return;
                  if (typeof dateInputRef.current.showPicker === 'function') {
                    dateInputRef.current.showPicker();
                  } else {
                    dateInputRef.current.focus();
                  }
                }}
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="ccButtonRow">
            <button
              type="button"
              className="oc-btn oc-btn-secondary"
              onClick={handleReset}
              disabled={loading}
            >
              {isModal ? 'Cancel' : 'Reset'}
            </button>
            <button
              type="submit"
              className="oc-btn"
              disabled={!isFormValid || loading}
            >
              {loading ? 'Processing...' : 'Post Earnings'}
            </button>
          </div>

        </form>

        {/* Backend error */}
        {errorMessage && (
          <div className="ccBackendError">
            <div className="ccBackendErrorIcon">✕</div>
            <div className="ccBackendErrorContent">
              <div className="ccBackendErrorTitle">
                {errorMessage.code && errorMessage.message
                  ? `Error: ${errorMessage.code} is not valid`
                  : 'Error occurred'}
              </div>
              <div className="ccBackendErrorMessage">
                {errorMessage.message || 'Please enter a valid value'}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );

  if (isModal) return formContent;

  return (
    <div className="ccFullscreen">
      <div className="ccFullscreenBody">
        {formContent}
      </div>

      {showToast && toastData && (
        <div className={`ccToast${toastData.isError ? ' ccToastError' : ''}`} role="status" aria-live="polite">
          <div className="ccToastHeader">
            <span>{toastData.isError ? 'Post earnings failed' : 'Post Earnings Successful'}</span>
            <button className="ccToastCloseBtn" onClick={() => setShowToast(false)} aria-label="dismiss">×</button>
          </div>
          {!toastData.isError && (
            <div className="ccToastBody">
              {toastData.unity && <div>Unity points balance: <strong>{toastData.unity}</strong></div>}
              {toastData.tier  && <div>Tier points balance: <strong>{toastData.tier}</strong></div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
